/**
 * @file org-timezone-provider.tsx
 * @description React context providing the active organization's IANA timezone
 *              to all client components. Server layouts fetch the timezone from
 *              the session and pass it as a prop; client components read it via
 *              useOrgTimezone(). This ensures server and client render identical
 *              date/time strings, preventing hydration mismatches.
 * @author William Donaldson
 * @last_modified 2026-03-01
 */

"use client";

import { createContext, useContext, type ReactNode } from "react";

// WHY DEFAULT "America/New_York": Matches the Organization.timezone schema default.
// If the provider is missing (e.g. super-admin with no org), formatting still works.
const OrgTimezoneContext = createContext<string>("America/New_York");

interface OrgTimezoneProviderProps {
  timezone: string;
  children: ReactNode;
}

export function OrgTimezoneProvider({ timezone, children }: OrgTimezoneProviderProps) {
  return (
    <OrgTimezoneContext.Provider value={timezone}>
      {children}
    </OrgTimezoneContext.Provider>
  );
}

/**
 * Returns the active organization's IANA timezone string.
 * Must be used within an OrgTimezoneProvider (staff/portal layouts).
 *
 * Usage:
 *   const tz = useOrgTimezone();
 *   formatDateTime(event.createdAt, tz);
 */
export function useOrgTimezone(): string {
  return useContext(OrgTimezoneContext);
}
